import React from 'react';
import { Card } from '@/components/ui/cards/Card';
import { Skeleton } from '@/components/ui/loading';
import { Text } from '@/components/ui/text/Text';
import { useTranslation } from '@/i18n';
import { colors } from '@/assets/styles/colors';
import type { DashboardMark } from '@/api/domains/attendance';


interface TodayMarksCardProps {
  marks: DashboardMark[];
  isLoading?: boolean;
  hasError?: boolean;
  // Slot opcional renderizado en la esquina superior derecha de la tarjeta (p. ej. el
  // trigger de un `ActionMenu`). Mismo patrón que `AttendanceSituationCard`.
  headerAction?: React.ReactNode;
  'data-testid'?: string;
}

type MarkType = 'on_time' | 'late' | 'overtime';

const MARK_COLOR: Record<MarkType, string> = {
  on_time: colors.feedback.success300,
  late: colors.feedback.alert300,
  overtime: colors.important.main,
};

export const TodayMarksCard: React.FC<TodayMarksCardProps> = ({
  marks,
  isLoading = false,
  hasError = false,
  headerAction,
  'data-testid': dataTestId,
}) => {
  const { t: translate } = useTranslation();

  const legendItems = [
    { color: colors.feedback.success300, label: translate('attendance:dashboard.marks.legend.onTime') },
    { color: colors.feedback.alert300, label: translate('attendance:dashboard.marks.legend.late') },
    { color: colors.important.main, label: translate('attendance:dashboard.marks.legend.overtime') },
  ];

  if (isLoading) {
    return (
      <Card elevation={2} borderRadius="16px" padding="24px" data-testid={dataTestId}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
          <Skeleton width={140} height={16} data-testid={`${dataTestId}-skeleton-title`} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
            {[0, 1, 2].map((index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Skeleton
                  size={{ width: 10, height: 10 }}
                  className="rounded-full"
                  data-testid={`${dataTestId}-skeleton-dot-${index}`}
                />
                <Skeleton width="60%" height={14} data-testid={`${dataTestId}-skeleton-line-${index}`} />
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 24, marginTop: 'auto', flexWrap: 'wrap' }}>
            {[0, 1, 2].map((index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Skeleton size={{ width: 8, height: 8 }} data-testid={`${dataTestId}-skeleton-legend-dot-${index}`} />
                <Skeleton width={60} height={12} data-testid={`${dataTestId}-skeleton-legend-label-${index}`} />
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card elevation={2} borderRadius="16px" padding="24px" data-testid={dataTestId}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text
            variant="body-medium"
            fontFamily="montserrat"
            color={colors.grays.neutral66}
            style={{ fontWeight: 600 }}
            data-testid={`${dataTestId}-title`}
          >
            {translate('attendance:dashboard.marks.title')}
          </Text>
          {headerAction && (
            <div data-testid={`${dataTestId}-header-action`}>
              {headerAction}
            </div>
          )}
        </div>

        {hasError ? (
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}
            data-testid={`${dataTestId}-error`}
          >
            <Text variant="body-medium" color={colors.feedback.alert300}>
              {translate('attendance:dashboard.marks.loadErrorState')}
            </Text>
          </div>
        ) : marks.length === 0 ? (
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}
            data-testid={`${dataTestId}-empty`}
          >
            <Text variant="body-medium" color={colors.grays.neutral66}>
              {translate('attendance:dashboard.marks.noMarksToday')}
            </Text>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            {marks.map((mark, index) => (
              <div
                key={index}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                data-testid={`${dataTestId}-mark-${index}`}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: MARK_COLOR[mark.markType],
                    flexShrink: 0,
                  }}
                />
                <Text
                  variant="body-medium"
                  fontFamily="montserrat"
                  color={colors.grays.neutral66}
                  data-testid={`${dataTestId}-mark-${index}-text`}
                >
                  {`${mark.time} - ${mark.description}${mark.modality ? ` - ${mark.modality}` : ''}`}
                </Text>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, marginTop: 'auto', flexWrap: 'wrap' }}>
          {legendItems.map((item) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Text variant="small-regular" color={colors.grays.neutral66}>
                {item.label}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
